
import React from 'react';
import { AlertTriangle, RefreshCw, Home, Phone } from 'lucide-react';

interface ErrorRecoveryProps {
  error: Error;
  resetError: () => void;
  context?: string;
}

const ErrorRecovery: React.FC<ErrorRecoveryProps> = ({ 
  error, 
  resetError, 
  context = 'unknown' 
}) => {
  const handleRetry = () => {
    try {
      resetError();
    } catch (e) {
      console.error('ErrorRecovery: Failed to reset error:', e);
      window.location.reload();
    }
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  const handleContactSupport = () => {
    window.location.href = 'tel:+48602709327';
  };

  const getErrorSeverity = () => {
    if (error.message.includes('ChunkLoadError') || error.message.includes('Loading chunk')) {
      return 'network';
    }
    if (error.message.includes('TypeError') && error.message.includes('undefined')) {
      return 'data';
    }
    if (error.message.includes('Permission')) {
      return 'permission';
    }
    return 'generic';
  };

  const getRecoveryMessage = () => {
    const severity = getErrorSeverity();
    
    switch (severity) {
      case 'network':
        return {
          title: 'Problem z połączeniem',
          description: 'Sprawdź połączenie internetowe i spróbuj ponownie.',
          icon: React.createElement(RefreshCw, { className: "w-8 h-8 text-blue-500" })
        };
      case 'data':
        return {
          title: 'Problem z danymi',
          description: 'Wystąpił problem podczas ładowania danych. Spróbujemy ponownie.',
          icon: React.createElement(AlertTriangle, { className: "w-8 h-8 text-orange-500" })
        };
      case 'permission':
        return {
          title: 'Problem z uprawnieniami',
          description: 'Brak wymaganych uprawnień. Odśwież stronę lub skontaktuj się z nami.',
          icon: React.createElement(AlertTriangle, { className: "w-8 h-8 text-red-500" })
        };
      default:
        return {
          title: 'Wystąpił nieoczekiwany błąd',
          description: 'Przepraszamy za utrudnienia. Pracujemy nad rozwiązaniem problemu.',
          icon: React.createElement(AlertTriangle, { className: "w-8 h-8 text-red-500" })
        };
    }
  };

  const recoveryMessage = getRecoveryMessage();

  return React.createElement('div', {
    className: "min-h-[60vh] flex items-center justify-center p-6 bg-black"
  }, 
    React.createElement('div', {
      className: "max-w-md w-full bg-black rounded-2xl shadow-xl p-8 text-center border border-white/20"
    }, [
      React.createElement('div', {
        key: 'icon',
        className: "mb-6 flex justify-center"
      }, recoveryMessage.icon),
      
      React.createElement('h2', {
        key: 'title',
        className: "text-2xl font-bold text-white mb-4"
      }, recoveryMessage.title),
      
      React.createElement('p', {
        key: 'description',
        className: "text-white/70 mb-8"
      }, recoveryMessage.description),
      
      React.createElement('div', {
        key: 'buttons',
        className: "space-y-4"
      }, [
        React.createElement('button', {
          key: 'retry',
          onClick: handleRetry,
          className: "w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200"
        }, 'Spróbuj ponownie'),
        
        React.createElement('div', {
          key: 'nav-buttons',
          className: "grid grid-cols-2 gap-3"
        }, [
          React.createElement('button', {
            key: 'home',
            onClick: handleGoHome,
            className: "flex items-center justify-center gap-2 bg-black border border-white/20 hover:bg-white/10 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
          }, [
            React.createElement(Home, { key: 'home-icon', className: "w-4 h-4" }),
            'Strona główna'
          ]),
          
          React.createElement('button', {
            key: 'support',
            onClick: handleContactSupport,
            className: "flex items-center justify-center gap-2 bg-black border border-white/20 hover:bg-white/10 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
          }, [
            React.createElement(Phone, { key: 'phone-icon', className: "w-4 h-4" }),
            'Pomoc'
          ])
        ])
      ]),
      
      process.env.NODE_ENV === 'development' && React.createElement('details', {
        key: 'debug',
        className: "mt-8 text-left"
      }, [
        React.createElement('summary', {
          key: 'summary',
          className: "text-sm text-white/40 cursor-pointer"
        }, 'Informacje diagnostyczne'),
        React.createElement('pre', {
          key: 'pre',
          className: "mt-2 p-3 bg-black border border-white/10 rounded text-xs overflow-auto text-white/60"
        }, JSON.stringify({ 
          error: error.message, 
          context,
          timestamp: new Date().toISOString()
        }, null, 2))
      ])
    ])
  );
};

export default ErrorRecovery;
